/**
 * FilterRow (VOY-489)
 *
 * Pill-strip filter UI matching the Search+ design's `.filter-row`.
 * Replaces the panel-based `FilterBar` (flights) + `HotelFilterBar`
 * (hotels) chrome WITHOUT changing any filter behavior. Sliders,
 * checkboxes, and other Popover internals stay identical — only the
 * trigger buttons are restyled to `.filter-pill`s that morph between
 * inactive (name + ▾) and active (value + ×).
 *
 * Two exports because the flight and hotel filter state shapes differ
 * fundamentally:
 *
 *   - `<FilterRowFlights>` reads `FlightResult[]` directly and emits
 *     filtered flights via `onFiltersChange` (same contract as the
 *     old FilterBar). State lives inside this component.
 *
 *   - `<FilterRowHotels>` is controlled — `filters` + `onChange` come
 *     from the parent (same contract as the old HotelFilterBar). Sort
 *     is also threaded through `filters.sortBy`, so the sort select
 *     moved out of HotelTabContent and into this component.
 */
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FlightResult, Flight, MultiSliceFlight } from '@/types/travel';

// ============================================================
// Shared atoms
// ============================================================

interface FilterPillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  /** Pill label. When active, callers usually format as "Name $value". */
  children: React.ReactNode;
  /** Only shown when `active`. Stops propagation so the popover doesn't open. */
  onClear?: () => void;
  /** ARIA label override; defaults to the rendered text. */
  ariaLabel?: string;
}

/**
 * VOY-489 fix: forwardRef + `...rest` spread so that
 * `<PopoverTrigger asChild>` can inject its toggle handler, ref, and
 * `aria-*` attributes onto the underlying button. Without this, Radix's
 * cloneElement-based asChild pattern silently drops the props it adds
 * (function components have no DOM element for cloneElement to mutate
 * unless we explicitly forward).
 */
const FilterPill = forwardRef<HTMLButtonElement, FilterPillProps>(function FilterPill(
  { active, children, onClear, ariaLabel, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn('filter-pill', active && 'active', className)}
      aria-label={ariaLabel}
      {...rest}
    >
      <span>{children}</span>
      {active ? (
        // Can't use a real <button> here because the pill itself IS a
        // button (nested buttons = invalid HTML, inconsistent across
        // browsers). Use role="button" + tabIndex={-1} + a keydown
        // handler that mirrors the click — gives keyboard activation
        // (Space/Enter) without breaking the outer button semantics.
        <span
          role="button"
          tabIndex={-1}
          className="pill-clear"
          aria-label="Clear filter"
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              onClear?.();
            }
          }}
        >
          <X aria-hidden="true" />
        </span>
      ) : (
        <ChevronDown aria-hidden="true" />
      )}
    </button>
  );
});

// ============================================================
// Helpers — flight (mirrored from old FilterBar.tsx verbatim)
// ============================================================

interface TimeRange {
  min: number;
  max: number;
}

interface PriceRange {
  min: number;
  max: number;
}

interface DurationRange {
  min: number; // minutes
  max: number;
}

const parseDurationToMinutes = (duration: string): number => {
  const hourMatch = duration.match(/(\d+)h/);
  const minuteMatch = duration.match(/(\d+)m/);
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
  return hours * 60 + minutes;
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const getDepartureHour = (flight: FlightResult): number => {
  let departureTime: string | Date;
  if ('type' in flight && flight.type === 'multi-slice') {
    departureTime = (flight as MultiSliceFlight).slices[0].departureTime;
  } else {
    departureTime = (flight as Flight).departureTime;
  }
  return new Date(departureTime).getHours();
};

const getArrivalHour = (flight: FlightResult): number => {
  let arrivalTime: string | Date;
  if ('type' in flight && flight.type === 'multi-slice') {
    const slices = (flight as MultiSliceFlight).slices;
    arrivalTime = slices[slices.length - 1].arrivalTime;
  } else {
    arrivalTime = (flight as Flight).arrivalTime;
  }
  return new Date(arrivalTime).getHours();
};

const formatHour = (hour: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}${period}`;
};
const formatTimeRange = (min: number, max: number): string =>
  `${formatHour(min)}–${formatHour(max)}`;

// ============================================================
// FilterRowFlights
// ============================================================

export type FlightSortBy = 'best' | 'price-asc' | 'price-desc' | 'duration' | 'departure';

interface FilterRowFlightsProps {
  flights: FlightResult[];
  onFiltersChange: (filtered: FlightResult[]) => void;
  sortBy?: FlightSortBy;
  onSortChange?: (sortBy: FlightSortBy) => void;
}

export function FilterRowFlights({
  flights,
  onFiltersChange,
  sortBy = 'best',
  onSortChange,
}: FilterRowFlightsProps) {
  // Committed filter state — drives the filter-and-emit useEffect.
  const [selectedAirlines, setSelectedAirlines] = useState<Set<string>>(new Set());
  const [selectedAirports, setSelectedAirports] = useState<Set<string>>(new Set());
  const [departureTimeRange, setDepartureTimeRange] = useState<TimeRange>({ min: 0, max: 24 });
  const [arrivalTimeRange, setArrivalTimeRange] = useState<TimeRange>({ min: 0, max: 24 });
  const [priceRange, setPriceRange] = useState<PriceRange | null>(null);
  const [durationRange, setDurationRange] = useState<DurationRange | null>(null);

  // VOY-489 follow-up: pending state per popover dimension. Apply
  // button commits pending → committed; closing without Apply
  // discards (resyncs on next open). Same pattern as FilterRowHotels.
  const [pendingAirlines, setPendingAirlines] = useState<Set<string>>(new Set());
  const [pendingAirports, setPendingAirports] = useState<Set<string>>(new Set());
  const [pendingDepartureTimeRange, setPendingDepartureTimeRange] = useState<TimeRange>({ min: 0, max: 24 });
  const [pendingArrivalTimeRange, setPendingArrivalTimeRange] = useState<TimeRange>({ min: 0, max: 24 });
  const [pendingPriceRange, setPendingPriceRange] = useState<PriceRange | null>(null);
  const [pendingDurationRange, setPendingDurationRange] = useState<DurationRange | null>(null);

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleOpenChange = (dim: string) => (open: boolean) => {
    if (open) {
      // Resync pending from committed on each open.
      if (dim === 'airlines') setPendingAirlines(new Set(selectedAirlines));
      else if (dim === 'airports') setPendingAirports(new Set(selectedAirports));
      else if (dim === 'times') {
        setPendingDepartureTimeRange(departureTimeRange);
        setPendingArrivalTimeRange(arrivalTimeRange);
      } else if (dim === 'duration') setPendingDurationRange(durationRange);
      else if (dim === 'price') setPendingPriceRange(priceRange);
      setOpenDropdown(dim);
    } else {
      setOpenDropdown(null);
    }
  };

  // --- Apply handlers (pending → committed + close) ---
  const applyAirlines = () => {
    setSelectedAirlines(new Set(pendingAirlines));
    setOpenDropdown(null);
  };
  const applyAirports = () => {
    setSelectedAirports(new Set(pendingAirports));
    setOpenDropdown(null);
  };
  const applyTimes = () => {
    setDepartureTimeRange(pendingDepartureTimeRange);
    setArrivalTimeRange(pendingArrivalTimeRange);
    setOpenDropdown(null);
  };
  const applyDuration = () => {
    if (pendingDurationRange) setDurationRange(pendingDurationRange);
    setOpenDropdown(null);
  };
  const applyPrice = () => {
    if (pendingPriceRange) setPriceRange(pendingPriceRange);
    setOpenDropdown(null);
  };

  // Derive bounds from current flight set — same as old FilterBar.
  const filterOptions = useMemo(() => {
    const airlines = new Map<string, number>();
    const airports = new Map<string, number>();
    let minPrice = Infinity;
    let maxPrice = 0;
    let minDuration = Infinity;
    let maxDuration = 0;

    flights.forEach((flight) => {
      const price = 'totalPrice' in flight ? flight.totalPrice : flight.price;
      minPrice = Math.min(minPrice, price);
      maxPrice = Math.max(maxPrice, price);

      if ('type' in flight && flight.type === 'multi-slice') {
        const multiSlice = flight as MultiSliceFlight;
        multiSlice.slices.forEach((slice) => {
          airlines.set(slice.airline, (airlines.get(slice.airline) || 0) + 1);
          airports.set(slice.origin, (airports.get(slice.origin) || 0) + 1);
          airports.set(slice.destination, (airports.get(slice.destination) || 0) + 1);
          const d = parseDurationToMinutes(slice.duration);
          minDuration = Math.min(minDuration, d);
          maxDuration = Math.max(maxDuration, d);
        });
      } else {
        const sf = flight as Flight;
        airlines.set(sf.airline, (airlines.get(sf.airline) || 0) + 1);
        airports.set(sf.origin, (airports.get(sf.origin) || 0) + 1);
        airports.set(sf.destination, (airports.get(sf.destination) || 0) + 1);
        const d = parseDurationToMinutes(sf.duration);
        minDuration = Math.min(minDuration, d);
        maxDuration = Math.max(maxDuration, d);
      }
    });

    return {
      airlines: Array.from(airlines.entries())
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count),
      airports: Array.from(airports.entries())
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count),
      priceRange: { min: minPrice === Infinity ? 0 : minPrice, max: maxPrice },
      durationRange: { min: minDuration === Infinity ? 0 : minDuration, max: maxDuration },
    };
  }, [flights]);

  // Initialise sliders once data arrives.
  useEffect(() => {
    if (!priceRange && filterOptions.priceRange) {
      setPriceRange(filterOptions.priceRange);
    }
    if (!durationRange && filterOptions.durationRange) {
      setDurationRange(filterOptions.durationRange);
    }
  }, [filterOptions, priceRange, durationRange]);

  // Apply filters — verbatim from old FilterBar.
  useEffect(() => {
    const filtered = flights.filter((flight) => {
      if (selectedAirlines.size > 0) {
        const flightAirlines = new Set<string>();
        if ('type' in flight && flight.type === 'multi-slice') {
          (flight as MultiSliceFlight).slices.forEach((s) => flightAirlines.add(s.airline));
        } else {
          flightAirlines.add((flight as Flight).airline);
        }
        if (!Array.from(selectedAirlines).some((a) => flightAirlines.has(a))) return false;
      }

      if (selectedAirports.size > 0) {
        const flightAirports = new Set<string>();
        if ('type' in flight && flight.type === 'multi-slice') {
          (flight as MultiSliceFlight).slices.forEach((s) => {
            flightAirports.add(s.origin);
            flightAirports.add(s.destination);
          });
        } else {
          const sf = flight as Flight;
          flightAirports.add(sf.origin);
          flightAirports.add(sf.destination);
        }
        if (!Array.from(selectedAirports).some((a) => flightAirports.has(a))) return false;
      }

      if (priceRange) {
        const price = 'totalPrice' in flight ? flight.totalPrice : flight.price;
        if (price < priceRange.min || price > priceRange.max) return false;
      }

      if (durationRange) {
        let flightDuration = 0;
        if ('type' in flight && flight.type === 'multi-slice') {
          const ds = (flight as MultiSliceFlight).slices.map((s) => parseDurationToMinutes(s.duration));
          flightDuration = Math.max(...ds);
        } else {
          flightDuration = parseDurationToMinutes((flight as Flight).duration);
        }
        if (flightDuration < durationRange.min || flightDuration > durationRange.max) return false;
      }

      if (departureTimeRange.min !== 0 || departureTimeRange.max !== 24) {
        const h = getDepartureHour(flight);
        if (h < departureTimeRange.min || h > departureTimeRange.max) return false;
      }

      if (arrivalTimeRange.min !== 0 || arrivalTimeRange.max !== 24) {
        const h = getArrivalHour(flight);
        if (h < arrivalTimeRange.min || h > arrivalTimeRange.max) return false;
      }

      return true;
    });

    onFiltersChange(filtered);
  }, [
    flights,
    selectedAirlines,
    selectedAirports,
    priceRange,
    durationRange,
    departureTimeRange,
    arrivalTimeRange,
    onFiltersChange,
  ]);

  // Active flags per dimension (drive pill rendering).
  const airlinesActive = selectedAirlines.size > 0;
  const airportsActive = selectedAirports.size > 0;
  const timesActive =
    departureTimeRange.min !== 0 ||
    departureTimeRange.max !== 24 ||
    arrivalTimeRange.min !== 0 ||
    arrivalTimeRange.max !== 24;
  const priceActive =
    !!priceRange &&
    (priceRange.min !== filterOptions.priceRange.min ||
      priceRange.max !== filterOptions.priceRange.max);
  const durationActive =
    !!durationRange &&
    (durationRange.min !== filterOptions.durationRange.min ||
      durationRange.max !== filterOptions.durationRange.max);

  const hasAny = airlinesActive || airportsActive || timesActive || priceActive || durationActive;

  const resetAll = () => {
    setSelectedAirlines(new Set());
    setSelectedAirports(new Set());
    setDepartureTimeRange({ min: 0, max: 24 });
    setArrivalTimeRange({ min: 0, max: 24 });
    setPriceRange(filterOptions.priceRange);
    setDurationRange(filterOptions.durationRange);
  };

  // All/None act on the PENDING set inside the open popover so the
  // user can still hit Apply to commit (or Cancel by clicking outside).
  const handleSelectAll = (filterType: 'airlines' | 'airports') => {
    if (filterType === 'airlines') {
      setPendingAirlines(new Set(filterOptions.airlines.map((a) => a.value)));
    } else {
      setPendingAirports(new Set(filterOptions.airports.map((a) => a.value)));
    }
  };
  const handleSelectNone = (filterType: 'airlines' | 'airports') => {
    if (filterType === 'airlines') setPendingAirlines(new Set());
    else setPendingAirports(new Set());
  };

  // ---- pill labels (active-state value formatting) ----
  const airlinesLabel = airlinesActive
    ? `Airlines (${selectedAirlines.size})`
    : 'Airlines';
  const airportsLabel = airportsActive ? `Airports (${selectedAirports.size})` : 'Airports';
  const timesLabel = timesActive
    ? `Dep ${formatTimeRange(departureTimeRange.min, departureTimeRange.max)} · Arr ${formatTimeRange(arrivalTimeRange.min, arrivalTimeRange.max)}`
    : 'Times';
  const priceLabel =
    priceActive && priceRange
      ? `Price $${Math.round(priceRange.min)}–$${Math.round(priceRange.max)}`
      : 'Price';
  const durationLabel =
    durationActive && durationRange
      ? `Duration ${formatDuration(durationRange.min)}–${formatDuration(durationRange.max)}`
      : 'Duration';

  return (
    <div className="filter-row" role="toolbar" aria-label="Flight filters">
      <span className="filter-row-label">Filter</span>

      {/* Airlines */}
      <Popover open={openDropdown === 'airlines'} onOpenChange={handleOpenChange('airlines')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={airlinesActive}
            onClear={() => {
              setPendingAirlines(new Set());
              setSelectedAirlines(new Set());
            }}
          >
            {airlinesLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0 rounded-2xl" align="start">
          <div className="p-2 border-b">
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => handleSelectAll('airlines')} className="rounded-full">
                All
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleSelectNone('airlines')} className="rounded-full">
                None
              </Button>
            </div>
          </div>
          <ScrollArea className="h-64">
            <div className="p-2 space-y-1">
              {filterOptions.airlines.map((a) => (
                <label key={a.value} className="flex items-center space-x-2 p-2 hover:bg-accent rounded cursor-pointer">
                  <Checkbox
                    checked={pendingAirlines.has(a.value)}
                    onCheckedChange={(checked: boolean) => {
                      const next = new Set(pendingAirlines);
                      if (checked) next.add(a.value);
                      else next.delete(a.value);
                      setPendingAirlines(next);
                    }}
                  />
                  <span className="flex-1 text-sm">{a.label}</span>
                  <span className="text-xs text-muted-foreground">({a.count})</span>
                </label>
              ))}
            </div>
          </ScrollArea>
          <div className="filter-popover-footer" style={{ padding: '8px 12px', borderTop: '1px solid var(--v6-hair)' }}>
            <button type="button" className="filter-popover-apply" onClick={applyAirlines}>
              Apply
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Airports */}
      <Popover open={openDropdown === 'airports'} onOpenChange={handleOpenChange('airports')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={airportsActive}
            onClear={() => {
              setPendingAirports(new Set());
              setSelectedAirports(new Set());
            }}
          >
            {airportsLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0 rounded-2xl" align="start">
          <div className="p-2 border-b">
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => handleSelectAll('airports')} className="rounded-full">
                All
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleSelectNone('airports')} className="rounded-full">
                None
              </Button>
            </div>
          </div>
          <ScrollArea className="h-64">
            <div className="p-2 space-y-1">
              {filterOptions.airports.map((a) => (
                <label key={a.value} className="flex items-center space-x-2 p-2 hover:bg-accent rounded cursor-pointer">
                  <Checkbox
                    checked={pendingAirports.has(a.value)}
                    onCheckedChange={(checked: boolean) => {
                      const next = new Set(pendingAirports);
                      if (checked) next.add(a.value);
                      else next.delete(a.value);
                      setPendingAirports(next);
                    }}
                  />
                  <span className="flex-1 text-sm">{a.label}</span>
                  <span className="text-xs text-muted-foreground">({a.count})</span>
                </label>
              ))}
            </div>
          </ScrollArea>
          <div className="filter-popover-footer" style={{ padding: '8px 12px', borderTop: '1px solid var(--v6-hair)' }}>
            <button type="button" className="filter-popover-apply" onClick={applyAirports}>
              Apply
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Times */}
      <Popover open={openDropdown === 'times'} onOpenChange={handleOpenChange('times')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={timesActive}
            onClear={() => {
              setDepartureTimeRange({ min: 0, max: 24 });
              setArrivalTimeRange({ min: 0, max: 24 });
              setPendingDepartureTimeRange({ min: 0, max: 24 });
              setPendingArrivalTimeRange({ min: 0, max: 24 });
            }}
          >
            {timesLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-2xl" align="start">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Departure time</Label>
              <div className="mt-2 space-y-2">
                <div className="text-xs text-muted-foreground">
                  {formatTimeRange(pendingDepartureTimeRange.min, pendingDepartureTimeRange.max)}
                </div>
                <Slider
                  min={0}
                  max={24}
                  step={1}
                  value={[pendingDepartureTimeRange.min, pendingDepartureTimeRange.max]}
                  onValueChange={([min, max]: number[]) =>
                    setPendingDepartureTimeRange({ min, max })
                  }
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Arrival time</Label>
              <div className="mt-2 space-y-2">
                <div className="text-xs text-muted-foreground">
                  {formatTimeRange(pendingArrivalTimeRange.min, pendingArrivalTimeRange.max)}
                </div>
                <Slider
                  min={0}
                  max={24}
                  step={1}
                  value={[pendingArrivalTimeRange.min, pendingArrivalTimeRange.max]}
                  onValueChange={([min, max]: number[]) =>
                    setPendingArrivalTimeRange({ min, max })
                  }
                />
              </div>
            </div>
            <div className="filter-popover-footer">
              <button type="button" className="filter-popover-apply" onClick={applyTimes}>
                Apply
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Duration */}
      <Popover open={openDropdown === 'duration'} onOpenChange={handleOpenChange('duration')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={durationActive}
            onClear={() => {
              setDurationRange(filterOptions.durationRange);
              setPendingDurationRange(filterOptions.durationRange);
            }}
          >
            {durationLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-2xl" align="start">
          {pendingDurationRange && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Flight duration</span>
                <span className="text-muted-foreground">
                  {formatDuration(pendingDurationRange.min)} – {formatDuration(pendingDurationRange.max)}
                </span>
              </div>
              <Slider
                min={filterOptions.durationRange.min}
                max={filterOptions.durationRange.max}
                step={15}
                value={[pendingDurationRange.min, pendingDurationRange.max]}
                onValueChange={([min, max]: number[]) =>
                  setPendingDurationRange({ min, max })
                }
              />
              <div className="filter-popover-footer">
                <button type="button" className="filter-popover-apply" onClick={applyDuration}>
                  Apply
                </button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Price */}
      <Popover open={openDropdown === 'price'} onOpenChange={handleOpenChange('price')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={priceActive}
            onClear={() => {
              setPriceRange(filterOptions.priceRange);
              setPendingPriceRange(filterOptions.priceRange);
            }}
          >
            {priceLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-2xl" align="start">
          {pendingPriceRange && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Price range</span>
                <span className="text-muted-foreground">
                  ${Math.round(pendingPriceRange.min)} – ${Math.round(pendingPriceRange.max)}
                </span>
              </div>
              <Slider
                min={filterOptions.priceRange.min}
                max={filterOptions.priceRange.max}
                step={10}
                value={[pendingPriceRange.min, pendingPriceRange.max]}
                onValueChange={([min, max]: number[]) =>
                  setPendingPriceRange({ min, max })
                }
              />
              <div className="filter-popover-footer">
                <button type="button" className="filter-popover-apply" onClick={applyPrice}>
                  Apply
                </button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {hasAny && (
        <button
          type="button"
          className="filter-clear-all"
          onClick={resetAll}
          aria-label="Clear all filters"
        >
          Clear all
        </button>
      )}

      <span className="filter-row-spacer" />

      {/* Sort */}
      {onSortChange && (
        <select
          className="sort-select"
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as FlightSortBy)}
          aria-label="Sort flights"
        >
          <option value="best">Sort: Best match</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="duration">Duration</option>
          <option value="departure">Departure time</option>
        </select>
      )}
    </div>
  );
}

// ============================================================
// FilterRowHotels
// ============================================================

export interface HotelFilters {
  searchQuery?: string;
  stars?: number[];
  priceMin?: number;
  priceMax?: number;
  amenities?: string[];
  guestRating?: number;
  freeCancellation?: boolean;
  sortBy?: 'recommended' | 'price-asc' | 'price-desc' | 'rating';
}

interface FilterRowHotelsProps {
  filters: HotelFilters;
  onChange: (filters: HotelFilters) => void;
  onClear: () => void;
  disabled?: boolean;
  currency?: string;
  priceMin?: number;
  priceMax?: number;
  availableAmenities?: string[];
}

const COMMON_AMENITIES = [
  'wifi', 'pool', 'spa', 'gym', 'restaurant', 'bar', 'parking',
  'airport shuttle', 'room service', 'concierge', 'business center',
  'laundry', 'pets allowed', 'non-smoking', 'accessible', 'elevator',
  '24-hour front desk', 'luggage storage', 'tour desk',
];

export function FilterRowHotels({
  filters,
  onChange,
  onClear,
  disabled = false,
  currency = 'USD',
  priceMin = 0,
  priceMax = 1000,
  availableAmenities = [],
}: FilterRowHotelsProps) {
  const actualMin = Math.floor(priceMin);
  const actualMax = Math.ceil(priceMax);

  // VOY-489 follow-up: switched from "emit on every click" to "stage
  // pending state, commit on Apply" so each dropdown produces ONE
  // refetch instead of one per tick. Toggle pills (Free cancellation),
  // × clear, and sort still fire immediately — those are single-
  // action commits where staging adds no value.
  //
  // Each pending slot mirrors a committed dimension; the popover's
  // onOpenChange resyncs from committed on open and resets on close
  // without Apply (so cancelling via outside-click discards staging).
  const [pendingPriceRange, setPendingPriceRange] = useState<[number, number]>([
    filters.priceMin ?? actualMin,
    filters.priceMax ?? actualMax,
  ]);
  const [pendingStars, setPendingStars] = useState<number[]>(filters.stars ?? []);
  const [pendingGuestRating, setPendingGuestRating] = useState<number | undefined>(
    filters.guestRating,
  );
  const [pendingAmenities, setPendingAmenities] = useState<string[]>(
    filters.amenities ?? [],
  );
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Sync pending slots when their committed source changes externally
  // (e.g. parent calls onClear() to wipe all filters → committed goes
  // back to {} → pending should reflect that so the popover doesn't
  // resurrect stale staging on next open).
  useEffect(() => {
    if (filters.priceMin === undefined && filters.priceMax === undefined) {
      setPendingPriceRange([actualMin, actualMax]);
    }
  }, [actualMin, actualMax, filters.priceMin, filters.priceMax]);
  useEffect(() => {
    if (!filters.stars) setPendingStars([]);
  }, [filters.stars]);
  useEffect(() => {
    if (filters.guestRating === undefined) setPendingGuestRating(undefined);
  }, [filters.guestRating]);
  useEffect(() => {
    if (!filters.amenities) setPendingAmenities([]);
  }, [filters.amenities]);

  // Popover open handler — resyncs the relevant pending slot from
  // committed filters so the user always sees the canonical state
  // when they open a dropdown (even after Cancel-by-outside-click).
  const handleOpenChange = (dim: string) => (open: boolean) => {
    if (open) {
      if (dim === 'price') {
        setPendingPriceRange([
          filters.priceMin ?? actualMin,
          filters.priceMax ?? actualMax,
        ]);
      } else if (dim === 'stars') {
        setPendingStars(filters.stars ?? []);
      } else if (dim === 'rating') {
        setPendingGuestRating(filters.guestRating);
      } else if (dim === 'amenities') {
        setPendingAmenities(filters.amenities ?? []);
      }
      setOpenDropdown(dim);
    } else {
      setOpenDropdown(null);
    }
  };

  // --- Apply handlers (commit pending → onChange + close) ---
  const applyPrice = () => {
    const [min, max] = pendingPriceRange;
    const next = { ...filters };
    if (min > actualMin) next.priceMin = min;
    else delete next.priceMin;
    if (max < actualMax) next.priceMax = max;
    else delete next.priceMax;
    onChange(next);
    setOpenDropdown(null);
  };
  const applyStars = () => {
    if (pendingStars.length > 0) {
      onChange({ ...filters, stars: [...pendingStars].sort() });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { stars, ...rest } = filters;
      onChange(rest);
    }
    setOpenDropdown(null);
  };
  const applyGuestRating = () => {
    if (pendingGuestRating) {
      onChange({ ...filters, guestRating: pendingGuestRating });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { guestRating, ...rest } = filters;
      onChange(rest);
    }
    setOpenDropdown(null);
  };
  const applyAmenities = () => {
    if (pendingAmenities.length > 0) {
      onChange({ ...filters, amenities: pendingAmenities });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { amenities, ...rest } = filters;
      onChange(rest);
    }
    setOpenDropdown(null);
  };

  // --- Pending togglers (no upstream emit) ---
  const togglePendingStar = (star: number) => {
    setPendingStars((prev) =>
      prev.includes(star) ? prev.filter((s) => s !== star) : [...prev, star],
    );
  };
  const togglePendingAmenity = (a: string) => {
    setPendingAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
    );
  };

  // --- Immediate handlers (toggle pill + × clear pills + sort) ---
  // These don't have a popover so there's nothing to stage — single
  // click = single commit.
  const toggleFreeCancellation = () => {
    if (filters.freeCancellation) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { freeCancellation, ...rest } = filters;
      onChange(rest);
    } else {
      onChange({ ...filters, freeCancellation: true });
    }
  };

  const clearPrice = () => {
    setPendingPriceRange([actualMin, actualMax]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { priceMin: _pn, priceMax: _px, ...rest } = filters;
    onChange(rest);
  };
  const clearStars = () => {
    setPendingStars([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { stars, ...rest } = filters;
    onChange(rest);
  };
  const clearAmenities = () => {
    setPendingAmenities([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { amenities, ...rest } = filters;
    onChange(rest);
  };

  const priceActive = filters.priceMin !== undefined || filters.priceMax !== undefined;
  const starsActive = !!(filters.stars && filters.stars.length > 0);
  const ratingActive = filters.guestRating !== undefined;
  const cancellationActive = filters.freeCancellation === true;
  const amenitiesActive = !!(filters.amenities && filters.amenities.length > 0);

  const hasAny =
    priceActive || starsActive || ratingActive || cancellationActive || amenitiesActive;

  // Labels
  const priceLabel = priceActive
    ? `Price $${filters.priceMin ?? actualMin}–$${filters.priceMax ?? actualMax}`
    : 'Price';
  const starsLabel = starsActive ? `${filters.stars!.length} stars` : 'Stars';
  const ratingLabel = ratingActive ? `${filters.guestRating}+ rating` : 'Guest rating';
  const amenitiesLabel = amenitiesActive
    ? `Amenities (${filters.amenities!.length})`
    : 'Amenities';

  const allAmenities = Array.from(new Set([...COMMON_AMENITIES, ...availableAmenities]))
    .sort()
    .map((amenity) => ({
      value: amenity,
      label: amenity.charAt(0).toUpperCase() + amenity.slice(1).replace(/_/g, ' '),
      available: availableAmenities.includes(amenity),
    }));

  return (
    <div className="filter-row" role="toolbar" aria-label="Hotel filters">
      <span className="filter-row-label">Filter</span>

      {/* Price */}
      <Popover open={openDropdown === 'price'} onOpenChange={handleOpenChange('price')}>
        <PopoverTrigger asChild>
          <FilterPill active={priceActive} onClear={clearPrice}>
            {priceLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-xl" align="start">
          <div className="space-y-4">
            <Label className="text-sm font-medium">Price per night ({currency})</Label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500 min-w-[60px]">${pendingPriceRange[0]}</span>
              <Slider
                value={pendingPriceRange}
                onValueChange={(v) => setPendingPriceRange(v as [number, number])}
                min={actualMin}
                max={actualMax}
                step={Math.max(10, Math.round((actualMax - actualMin) / 100))}
                disabled={disabled}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 min-w-[70px]">${pendingPriceRange[1]}</span>
            </div>
            <div className="filter-popover-footer">
              <button
                type="button"
                className="filter-popover-apply"
                onClick={applyPrice}
                disabled={disabled}
              >
                Apply
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Stars */}
      <Popover open={openDropdown === 'stars'} onOpenChange={handleOpenChange('stars')}>
        <PopoverTrigger asChild>
          <FilterPill active={starsActive} onClear={clearStars}>
            {starsLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-4 rounded-xl" align="start">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Star rating</Label>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => (
                <div key={star} className="flex items-center space-x-2">
                  <Checkbox
                    id={`star-${star}`}
                    checked={pendingStars.includes(star)}
                    onCheckedChange={() => togglePendingStar(star)}
                    disabled={disabled}
                  />
                  <Label htmlFor={`star-${star}`} className="text-sm cursor-pointer">
                    {'★'.repeat(star)}{'☆'.repeat(5 - star)}
                  </Label>
                </div>
              ))}
            </div>
            <div className="filter-popover-footer">
              <button
                type="button"
                className="filter-popover-apply"
                onClick={applyStars}
                disabled={disabled}
              >
                Apply
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Guest rating */}
      <Popover open={openDropdown === 'rating'} onOpenChange={handleOpenChange('rating')}>
        <PopoverTrigger asChild>
          <FilterPill
            active={ratingActive}
            onClear={() => {
              setPendingGuestRating(undefined);
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { guestRating, ...rest } = filters;
              onChange(rest);
            }}
          >
            {ratingLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-4 rounded-xl" align="start">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Minimum rating</Label>
            <div className="space-y-1">
              {[
                { value: 9, label: '9+ Exceptional' },
                { value: 8, label: '8+ Excellent' },
                { value: 7, label: '7+ Very good' },
                { value: 6, label: '6+ Good' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setPendingGuestRating(
                      pendingGuestRating === opt.value ? undefined : opt.value,
                    )
                  }
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm rounded-lg transition-colors',
                    pendingGuestRating === opt.value
                      ? 'bg-gray-900 text-white'
                      : 'hover:bg-gray-100 text-gray-700',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="filter-popover-footer">
              <button
                type="button"
                className="filter-popover-apply"
                onClick={applyGuestRating}
                disabled={disabled}
              >
                Apply
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Free cancellation — toggle (no popover) */}
      <FilterPill
        active={cancellationActive}
        onClick={!disabled ? toggleFreeCancellation : undefined}
        onClear={!disabled ? toggleFreeCancellation : undefined}
        ariaLabel={
          cancellationActive ? 'Free cancellation: on, click to remove' : 'Free cancellation: off, click to add'
        }
      >
        Free cancellation
      </FilterPill>

      {/* Amenities */}
      <Popover open={openDropdown === 'amenities'} onOpenChange={handleOpenChange('amenities')}>
        <PopoverTrigger asChild>
          <FilterPill active={amenitiesActive} onClear={clearAmenities}>
            {amenitiesLabel}
          </FilterPill>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 rounded-xl" align="start">
          <div className="p-3 border-b">
            <Label className="text-sm font-medium">Amenities</Label>
          </div>
          <ScrollArea className="h-64">
            <div className="p-3 space-y-1">
              {allAmenities.map((a) => (
                <div
                  key={a.value}
                  className={cn(
                    'flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer',
                    !a.available && 'opacity-50',
                  )}
                >
                  <Checkbox
                    id={`amenity-${a.value}`}
                    checked={pendingAmenities.includes(a.value)}
                    onCheckedChange={() => togglePendingAmenity(a.value)}
                    disabled={disabled || !a.available}
                  />
                  <Label htmlFor={`amenity-${a.value}`} className="text-sm cursor-pointer flex-1">
                    {a.label}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="filter-popover-footer" style={{ padding: '8px 12px', borderTop: '1px solid var(--v6-hair)' }}>
            <button
              type="button"
              className="filter-popover-apply"
              onClick={applyAmenities}
              disabled={disabled}
            >
              Apply
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {hasAny && (
        <button
          type="button"
          className="filter-clear-all"
          onClick={onClear}
          aria-label="Clear all filters"
        >
          Clear all
        </button>
      )}

      <span className="filter-row-spacer" />

      {/* Sort */}
      <select
        className="sort-select"
        value={filters.sortBy ?? 'recommended'}
        onChange={(e) =>
          onChange({ ...filters, sortBy: e.target.value as HotelFilters['sortBy'] })
        }
        aria-label="Sort hotels"
      >
        <option value="recommended">Sort: Recommended</option>
        <option value="price-asc">Price: low to high</option>
        <option value="price-desc">Price: high to low</option>
        <option value="rating">Guest rating</option>
      </select>
    </div>
  );
}
